/**
 * Product Gallery Tabs - Switch between Features and Packages view
 * 
 * This script handles the switching between product media images (Features)
 * and variant package images (Packages) in the product gallery.
 */

class ProductGalleryTabs {
  constructor() {
    this.productData = null;
    this.currentTab = 'features';
    this.galleryElement = null;
    this.variantRenderFrame = null;
    this.variantSyncTimeouts = [];
    this.onVariantChanged = this.onVariantChanged.bind(this);
    this.onVariantInputChanged = this.onVariantInputChanged.bind(this);
    this.onVariantOptionChanged = this.onVariantOptionChanged.bind(this);
    this.onHistoryChanged = this.onHistoryChanged.bind(this);
    // this.thumbnailsElement = null; // Replaced by pageDotsData
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Get product data from the page
    this.loadProductData();
    
    // Set up event listeners for tab buttons
    this.setupTabButtons();
    
    // Store gallery elements
    this.galleryElement = document.querySelector('.product-gallery__media-list');
    
    // Capture all page-dots elements (thumbnails and mobile dots)
    this.pageDotsData = [];
    document.querySelectorAll('page-dots').forEach(el => {
      this.pageDotsData.push({
        currentElement: el, // Keep track of the current element in DOM
        originalOuterHTML: el.outerHTML, // Store original HTML to restore
        isThumbnail: el.classList.contains('product-gallery__thumbnail-list')
      });
    });

    // Store original gallery HTML for Features tab
    if (this.galleryElement) {
      this.originalGalleryHTML = this.galleryElement.innerHTML;
    }

    this.currentVariantId = this.getCurrentVariantId();
    this.setupVariantSync();
  }

  getVariantIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('variant');
  }

  getProductForms() {
    const forms = new Set();
    const formId = this.productData?.productFormId;

    if (formId) {
      const formByName = document.forms[formId];
      const formById = document.getElementById(formId);

      if (formByName instanceof HTMLFormElement) forms.add(formByName);
      if (formById instanceof HTMLFormElement) forms.add(formById);
    }

    document.querySelectorAll('form[action*="/cart/add"], form[id^="product-form-"]').forEach(form => forms.add(form));

    return Array.from(forms);
  }

  getVariantIdFromInputs() {
    const forms = this.getProductForms();

    for (const form of forms) {
      const idInput = form.querySelector('[name="id"]');
      if (idInput?.value) return String(idInput.value);
    }

    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"], [name="id"][form^="product-form-"]');
    return idInput?.value ? String(idInput.value) : null;
  }

  getVariantIdFromEvent(event) {
    const detail = event?.detail;

    if (detail?.variant?.id) return String(detail.variant.id);
    if (detail?.variantId) return String(detail.variantId);
    if (detail?.id) return String(detail.id);

    return null;
  }

  getCurrentVariantId(preferredVariantId = null) {
    return preferredVariantId || this.getVariantIdFromInputs() || this.getVariantIdFromUrl();
  }

  setupVariantSync() {
    const variantEvents = ['variant:change', 'product:variant-change', 'variant-change'];
    const forms = this.getProductForms();

    variantEvents.forEach(eventName => {
      document.addEventListener(eventName, this.onVariantChanged);
      forms.forEach(form => form.addEventListener(eventName, this.onVariantChanged));
    });

    document.addEventListener('change', this.onVariantInputChanged);
    document.addEventListener('click', this.onVariantOptionChanged);
    window.addEventListener('popstate', this.onHistoryChanged);
    window.addEventListener('product-gallery-tabs:url-change', this.onHistoryChanged);
    this.installHistorySync();
  }

  installHistorySync() {
    if (window.bqProductGalleryTabsHistoryReady) return;

    window.bqProductGalleryTabsHistoryReady = true;

    ['pushState', 'replaceState'].forEach(methodName => {
      const originalMethod = window.history[methodName];
      window.history[methodName] = function(...args) {
        const result = originalMethod.apply(this, args);
        window.dispatchEvent(new Event('product-gallery-tabs:url-change'));
        return result;
      };
    });
  }

  onVariantChanged(event) {
    const variantId = this.getCurrentVariantId(this.getVariantIdFromEvent(event));
    this.queueVariantGalleryUpdate(variantId, { force: true });
    this.scheduleVariantSync(true);
  }

  onVariantInputChanged(event) {
    const target = event.target;

    if (target?.name === 'id' || target?.matches?.('[data-option-position]') || target?.closest?.('variant-picker')) {
      this.scheduleVariantSync(true);
    }
  }

  onVariantOptionChanged(event) {
    const target = event.target;

    if (target?.matches?.('[data-option-position]') || target?.closest?.('variant-picker')) {
      this.scheduleVariantSync(true);
    }
  }

  onHistoryChanged() {
    this.queueVariantGalleryUpdate(this.getVariantIdFromUrl(), { force: true });
  }

  scheduleVariantSync(force = false) {
    [0, 50, 150, 300].forEach(delay => {
      const timeoutId = setTimeout(() => {
        this.variantSyncTimeouts = this.variantSyncTimeouts.filter(id => id !== timeoutId);
        this.queueVariantGalleryUpdate(this.getCurrentVariantId(), { force });
      }, delay);

      this.variantSyncTimeouts.push(timeoutId);
    });

    if (this.variantSyncTimeouts.length > 16) {
      this.variantSyncTimeouts.splice(0, this.variantSyncTimeouts.length - 16).forEach(timeoutId => clearTimeout(timeoutId));
    }
  }

  queueVariantGalleryUpdate(variantId, options = {}) {
    const force = Boolean(options.force);

    if (!variantId || (!force && variantId === this.currentVariantId)) {
      return;
    }

    this.currentVariantId = variantId;

    if (this.variantRenderFrame) {
      cancelAnimationFrame(this.variantRenderFrame);
    }

    // Let the swatch and product information paint before rebuilding gallery DOM.
    this.variantRenderFrame = requestAnimationFrame(() => {
      this.variantRenderFrame = null;
      this.handleVariantChange(variantId);
    });
  }

  handleVariantChange(variantId) {
    // Always switch to packages tab when variant changes
    if (this.currentTab !== 'packages') {
        // Find the button for packages tab
        const packagesBtn = document.querySelector('.product-gallery__tab-btn[data-tab="packages"]');
        if (packagesBtn) {
            this.switchTab('packages', packagesBtn, { force: true });
        } else {
            this.renderPackagesGallery(variantId);
        }
    } else {
        // If already on packages tab, just refresh/scroll
        this.renderPackagesGallery(variantId);
    }
  }


  loadProductData() {
    // Try to get product data from meta tag or global variable
    const productMetaTag = document.querySelector('meta[name="product-data"]');
    
    if (productMetaTag) {
      try {
        this.productData = JSON.parse(productMetaTag.content);
      } catch (e) {
        console.error('Failed to parse product data:', e);
      }
    }
    
    // Fallback: try to get from window object
    if (!this.productData && window.productGalleryData) {
      this.productData = window.productGalleryData;
    }
  }

  setupTabButtons() {
    const tabButtons = document.querySelectorAll('.product-gallery__tab-btn');
    
    tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const tabType = button.dataset.tab;
        this.switchTab(tabType, button);
      });
    });
  }

  switchTab(tabType, clickedButton, options = {}) {
    if (tabType === this.currentTab && !options.force) return;
    
    // Update button states
    document.querySelectorAll('.product-gallery__tab-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    if (clickedButton) clickedButton.classList.add('active');
    
    // Update current tab
    this.currentTab = tabType;
    
    // Render appropriate gallery content
    if (tabType === 'features') {
        this.renderFeaturesGallery(this.currentVariantId);
    } else if (tabType === 'packages') {
        this.renderPackagesGallery(this.currentVariantId);
    }
    
    // Trigger custom event for other components
    document.dispatchEvent(new CustomEvent('gallery:tab-changed', {
      detail: { tab: this.currentTab }
    }));
  }

  renderFeaturesGallery(variantId = null) {
    if (!this.galleryElement || !this.originalGalleryHTML) return;

    const targetImageId = variantId ? this.getTargetImageId(variantId) : null;
    
    // Restore original HTML (all product media)
    this.galleryElement.innerHTML = this.originalGalleryHTML;

    // Fix: Clear carousel internal cache and reset state
    this.resetCarouselState(targetImageId);
    
    // Restore all page dots/thumbnails
    this.restorePageDots();

    // Scroll logic
    let scrolled = false;
    if (targetImageId) {
      if (this.selectImageImmediately(targetImageId)) {
        scrolled = true;
      }
    }

    if (!scrolled) {
      this.galleryElement.scrollLeft = 0;
    }
  }

  renderPackagesGallery(variantId = null) {
    if (!this.galleryElement || !this.productData) return;
    
    // Get unique variant images
    const variantImages = this.getPackagesImages();
    
    if (variantImages.length === 0) return;

    const targetImageId = variantId ? this.getTargetImageId(variantId) : null;
    const activeImageId = targetImageId && variantImages.some(image => String(image.id) === String(targetImageId))
      ? String(targetImageId)
      : String(variantImages[0].id);
    
    // Build HTML for packages gallery (only variant images)
    let html = '';
    variantImages.forEach((image, index) => {
      const isActive = String(image.id) === activeImageId;
      html += `
        <div class="product-gallery__media snap-center${isActive ? ' is-selected' : ''}" data-media-type="image" data-media-id="${this.escapeAttribute(image.id)}">
          <img
            src="${this.escapeAttribute(image.src)}"
            alt="${this.escapeAttribute(image.alt)}"
            width="${this.escapeAttribute(image.width)}"
            height="${this.escapeAttribute(image.height)}"
            loading="${isActive || index === 0 ? 'eager' : 'lazy'}"
            class="object-contain w-full h-full"
            sizes="(max-width: 740px) calc(100vw - 40px), (max-width: 999px) calc(100vw - 64px), min(760px, 42vw)"
          />
        </div>
      `;
    });
    
    // Replace gallery content
    this.galleryElement.innerHTML = html;

    // Fix: Clear carousel internal cache and reset state to prevent max-height: 0px issue
    // because the cached items are now detached from DOM
    this.resetCarouselState(activeImageId);
    
    // Replace thumbnails/dots
    this.updatePageDots(variantImages, activeImageId);
    
    // Scroll to current variant if specified
    let scrolled = false;
    if (activeImageId) {
      scrolled = this.selectImageImmediately(activeImageId);
    }

    if (!scrolled) {
      this.galleryElement.scrollLeft = 0;
    }
  }
  
  restorePageDots() {
    if (!this.pageDotsData) return;
    
    this.pageDotsData.forEach(data => {
        // Create element from original HTML to restore state (including event listeners if they were re-initialized by custom element lifecycle)
        const newEl = this.createElementFromHTML(data.originalOuterHTML);
        
        if (document.body.contains(data.currentElement)) {
          data.currentElement.replaceWith(newEl);
          data.currentElement = newEl;
        } else {
            // Fallback: try to find it by some other means or just skip if it's gone
            // But usually it should be there.
        }
    });
  }

  updatePageDots(variantImages, activeImageId = null) {
    if (!this.pageDotsData) return;

    const activeIndex = variantImages.findIndex(image => String(image.id) === String(activeImageId));

    this.pageDotsData.forEach(data => {
        // Create a template element from original HTML to preserve attributes
        const templateEl = this.createElementFromHTML(data.originalOuterHTML);
        templateEl.innerHTML = ''; // Clear children
        
        let newInnerHtml = '';
    variantImages.forEach((image, index) => {
             if (data.isThumbnail) {
                 newInnerHtml += this.getThumbnailButtonHtml(image, index, activeImageId);
             } else {
                 newInnerHtml += this.getDotButtonHtml(index, activeIndex);
             }
        });
        
        templateEl.innerHTML = newInnerHtml;
        
        if (document.body.contains(data.currentElement)) {
            data.currentElement.replaceWith(templateEl);
            data.currentElement = templateEl;
        }
    });
  }

  getThumbnailButtonHtml(image, index, activeImageId = null) {
      const isActive = String(image.id) === String(activeImageId);
      // Replicating the thumbnail structure
      // Note: image.src is the full URL. 
      return `
        <button type="button" class="product-gallery__thumbnail" data-media-id="${this.escapeAttribute(image.id)}" aria-current="${isActive ? 'true' : 'false'}" aria-label="Go to item ${index + 1}">
          <img src="${this.escapeAttribute(image.src)}" class="object-contain rounded-sm" loading="lazy" width="64" height="64" alt="${this.escapeAttribute(image.alt || '')}">
        </button>
      `;
  }

  getDotButtonHtml(index, activeIndex = 0) {
      // Replicating the dot structure
      return `
        <button type="button" class="tap-area" aria-current="${index === activeIndex ? 'true' : 'false'}">
          <span class="sr-only">Go to item ${index + 1}</span>
        </button>
      `;
  }

  createElementFromHTML(htmlString) {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div.firstElementChild;
  }

  resetCarouselState(mediaId = null) {
    if (!this.galleryElement) return;

    const slides = Array.from(this.galleryElement.children);
    const selectedIndex = mediaId
      ? Math.max(0, slides.findIndex(slide => String(slide.dataset.mediaId) === String(mediaId)))
      : 0;

    this.galleryElement._items = null;
    this.galleryElement._selectedIndex = selectedIndex;
    this.galleryElement.style.maxHeight = '';
    slides.forEach((slide, index) => {
      slide.classList.toggle('is-selected', index === selectedIndex);
    });
  }

  selectImageImmediately(mediaId) {
    const selected = this.scrollToImage(mediaId);

    if (selected) {
      requestAnimationFrame(() => this.scrollToImage(mediaId));
    }

    return selected;
  }

  escapeAttribute(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  normalizeImageSrc(src) {
    if (!src) return '';

    try {
      return new URL(src, window.location.origin).pathname;
    } catch (error) {
      return String(src).replace(/^https?:/, '').split('?')[0];
    }
  }

  findMediaBySrc(src) {
    if (!src || !this.productData?.media) return null;

    const normalizedSrc = this.normalizeImageSrc(src);
    return this.productData.media.find(media => {
      return media.src === src || this.normalizeImageSrc(media.src) === normalizedSrc;
    }) || null;
  }

  getPackagesImages() {
    if (!this.productData || !this.productData.variants) return [];
    
    // Get unique variant images (all of them)
    const variantImages = [];
    const seenSrcs = new Set();
    
    this.productData.variants.forEach(variant => {
      if (variant.featured_image && variant.featured_image.src) {
        const src = variant.featured_image.src;
        const normalizedSrc = this.normalizeImageSrc(src);
        
        // Only add if we haven't seen this image before
        if (!seenSrcs.has(normalizedSrc)) {
          seenSrcs.add(normalizedSrc);
          
          // Try to get Image ID directly, or look it up by src
          let imgId = variant.featured_image.id;
          let width = variant.featured_image.width;
          let height = variant.featured_image.height;
          const matchedMedia = this.findMediaBySrc(src);
          
          if (matchedMedia) {
            if (matchedMedia.id) imgId = matchedMedia.id;
            if (!width) width = matchedMedia.width;
            if (!height) height = matchedMedia.height;
          }
          
          // If we still don't have an ID, we can't control it via data-media-id
          if (imgId) {
            variantImages.push({
              src: src,
              alt: variant.title || 'Package option',
              id: String(imgId),
              width: width || 1000,
              height: height || 1000
            });
          }
        }
      }
    });
    
    return variantImages;
  }


  getTargetImageId(variantId) {
    if (!this.productData || !variantId) return null;
    
    const variant = this.productData.variants.find(v => v.id == variantId);
    if (variant && variant.featured_image) {
      let targetImageId = null;
      
      // Fallback: find by src
      if (variant.featured_image.src && this.productData.media) {
        const match = this.findMediaBySrc(variant.featured_image.src);
        if (match) targetImageId = match.id;
      }

      if (!targetImageId) targetImageId = variant.featured_image.id;
      
      return targetImageId;
    }
    return null;
  }

  scrollToImage(mediaId) {
    if (!this.galleryElement) return false;
    
    const strMediaId = String(mediaId);
    const targetSlide = this.galleryElement.querySelector(`[data-media-id="${strMediaId}"]`);
    
    if (targetSlide) {
      const slides = Array.from(this.galleryElement.children);
      const index = slides.findIndex(slide => String(slide.dataset.mediaId) === strMediaId);

      this.galleryElement._items = null;

      if (index !== -1 && typeof this.galleryElement.select === 'function') {
        this.galleryElement.select(index, { animate: false, force: true });
      }

      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-selected', slideIndex === index);
      });

      if (typeof this.galleryElement.scrollTo === 'function') {
        this.galleryElement.scrollTo({ left: targetSlide.offsetLeft, behavior: 'auto' });
      } else {
        this.galleryElement.scrollLeft = targetSlide.offsetLeft;
      }
      
      // Update thumbnail active state in all page-dots elements
      // We need to find the current elements from our data
      if (this.pageDotsData) {
          this.pageDotsData.forEach(data => {
             const container = data.currentElement;
             if (!container) return;
             
             // Try to find by data-media-id (thumbnails)
             const thumbnails = container.querySelectorAll(`[data-media-id]`);
             if (thumbnails.length > 0) {
                 thumbnails.forEach(thumb => {
                    if (String(thumb.dataset.mediaId) === strMediaId) {
                        thumb.setAttribute('aria-current', 'true');
                    } else {
                        thumb.setAttribute('aria-current', 'false');
                    }
                 });
             } else {
                 // Try to find by index (dots)
                 // But we don't know the index of the mediaId easily without searching the gallery
                 // The gallery items have data-media-id.
                 
                 // Find index of target slide
                 // Note: this.galleryElement._items might be cleared, we need to query DOM
                 const slides = Array.from(this.galleryElement.children);
                 const index = slides.findIndex(slide => String(slide.dataset.mediaId) === strMediaId);
                 
                 if (index !== -1) {
                     const dots = container.querySelectorAll('button');
                     dots.forEach((dot, i) => {
                         dot.setAttribute('aria-current', i === index ? 'true' : 'false');
                     });
                 }
             }
          });
      }

      return true;
    }

    return false;
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.ProductGalleryTabs = ProductGalleryTabs;
  
  // Auto-initialize
  new ProductGalleryTabs();
}
